<template>
  <v-dialog v-model="display" :width="dialogWidth">
    <template #activator="{ on }">
      <Textfield
        :value="formattedDatetime"
        v-bind="$attrs"
        prepend-inner-icon="mdi-calendar-clock"
        v-on="on"
        @click:clear="clearHandler"
      >
        <template #progress>
          <slot name="progress">
            <v-progress-linear
              color="primary"
              indeterminate
              absolute
              height="2"
            ></v-progress-linear>
          </slot>
        </template>
      </Textfield>
    </template>

    <v-card>
      <v-card-text class="pa-0">
        <v-tabs fixed-tabs v-model="activeTab">
          <v-tab key="calendar">
            <slot name="dateIcon">
              <v-icon>mdi-calendar-month-outline</v-icon>
            </slot>
          </v-tab>
          <v-tab key="timer" :disabled="dateSelected">
            <slot name="timeIcon">
              <v-icon>mdi-clock-outline</v-icon>
            </slot>
          </v-tab>
          <v-tab-item key="calendar">
            <v-date-picker
              v-model="date"
              v-bind="datePickerProps"
              scrollable
              header-color="primary"
              color="primary"
              :min="minDate"
              :max="maxDate"
              @input="showTimePicker"
              full-width
              :first-day-of-week="1"
            ></v-date-picker>
          </v-tab-item>
          <v-tab-item key="timer">
            <v-time-picker
              ref="timer"
              class="v-time-picker-custom"
              format="24hr"
              scrollable
              color="primary"
              header-color="primary"
              v-model="time"
              :min="minTime"
              :max="maxTime"
              v-bind="timePickerProps"
              full-width
            ></v-time-picker>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <slot name="actions" :parent="this">
          <BaseDialogActions @cancel="clearHandler()" @continue="okHandler()" />
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { format, parse, parseISO, toDate as datefnsToDate } from 'date-fns';

const DEFAULT_DATE = '';
const DEFAULT_TIME = '00:00:00';
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
const DEFAULT_TIME_FORMAT = 'HH:mm:ss';
const DEFAULT_DIALOG_WIDTH = 340;

export default {
  name: 'v-datetime-picker',
  model: {
    prop: 'datetime',
    event: 'input',
  },
  props: {
    datetime: {
      type: [Date, Number, String],
      default: null,
    },
    dialogWidth: {
      type: Number,
      default: DEFAULT_DIALOG_WIDTH,
    },
    dateFormat: {
      type: String,
      default: 'dd MMM yyyy',
    },
    timeFormat: {
      type: String,
      default: 'HH:mm',
    },
    textFieldProps: {
      type: Object,
    },
    datePickerProps: {
      type: Object,
    },
    timePickerProps: {
      type: Object,
    },
    min: {
      type: [Date, Number, String],
      default: null,
    },
    max: {
      type: [Date, Number, String],
      default: null,
    },
    defaultTab: {
      // Can be 0 (for date) or 1 (for time)
      type: Number,
      default: 0,
    }
  },
  data() {
    return {
      display: false,
      activeTab: 0,
      date: DEFAULT_DATE,
      time: DEFAULT_TIME,
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    dateTimeFormat() {
      return this.dateFormat + ' ' + this.timeFormat;
    },
    defaultDateTimeFormat() {
      return DEFAULT_DATE_FORMAT + ' ' + DEFAULT_TIME_FORMAT;
    },
    formattedDatetime() {
      return this.selectedDatetime
        ? format(this.selectedDatetime, this.dateTimeFormat)
        : '';
    },
    selectedDatetime() {
      if (this.date && this.time) {
        let datetimeString = this.date + ' ' + this.time;
        if (this.time.length === 5) {
          datetimeString += ':00';
        }
        return parse(datetimeString, this.defaultDateTimeFormat, new Date());
      } else {
        return null;
      }
    },
    dateSelected() {
      return !this.date;
    },
    minDate() {
      if (this.min == undefined) return;
      return format(this.toDate(this.min), DEFAULT_DATE_FORMAT);
    },
    maxDate() {
      if (this.max == undefined) return;
      return format(this.toDate(this.max), DEFAULT_DATE_FORMAT);
    },
    minTime() {
      if (this.min == undefined || this.date !== this.minDate) return;
      return format(this.toDate(this.min), DEFAULT_TIME_FORMAT);
    },
    maxTime() {
      if (this.max == undefined || this.date !== this.maxDate) return;
      return format(this.toDate(this.max), DEFAULT_TIME_FORMAT);
    },
  },
  methods: {
    init() {
      this.activeTab = this.defaultTab;
      if (!this.datetime) return;

      const initDateTime = this.toDate(this.datetime);

      this.date = format(initDateTime, DEFAULT_DATE_FORMAT);
      this.time = format(initDateTime, DEFAULT_TIME_FORMAT);
    },
    toDate(datetime) {
      let normalizedDate;
      if (typeof datetime === 'string' || datetime instanceof String) {
        // see https://stackoverflow.com/a/9436948
        normalizedDate =
          parseISO(datetime) ||
          parse(datetime, this.dateTimeFormat, new Date());
      } else {
        normalizedDate = datefnsToDate(datetime);
      }
      return normalizedDate;
    },
    okHandler() {
      // submits Date object
      this.resetPicker();
      this.$emit('input', this.selectedDatetime);
    },
    clearHandler() {
      this.resetPicker();
      this.date = DEFAULT_DATE;
      this.time = DEFAULT_TIME;
      this.$emit('input', null);
    },
    resetPicker() {
      this.display = false;
      this.activeTab = 0;
      if (this.$refs.timer) {
        this.$refs.timer.selectingHour = true;
      }
    },
    showTimePicker() {
      this.activeTab = 1;
    },
  },
  watch: {
    datetime() {
      this.init();
    },
  },
};
</script>
